// ══════════════════════════════════════════════════════════════
// Static content for the Automated Report Module (Institute
// Automation — Report Dashboard). Ported from the Bootstrap mockup
// report-module - Copy.html so the React screens stay data-driven.
// ══════════════════════════════════════════════════════════════

import type { CSSProperties } from 'react';

export interface Pill {
    label: string;
    className: string;
    style?: CSSProperties;
}

// ── shared priority / scope pills ──────────────────────────────
const BOTH: Pill = { label: 'Both', className: 'badge bg-primary-subtle text-primary font-10 font-500 px-2 py-1 rounded-pill' };
const HIGH: Pill = { label: 'High', className: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-1 rounded-pill' };
const MEDIUM: Pill = { label: 'Medium', className: 'badge bg-warning-subtle text-warning-emphasis font-10 font-500 px-2 py-1' };
const LOW: Pill = { label: 'Low', className: 'badge bg-light text-secondary font-10 font-500 px-2 py-1' };
const K12: Pill = { label: 'K-12 Only', className: 'badge font-10 font-500 px-2 py-1', style: { backgroundColor: '#ede9fe', color: '#7c3aed' } };

export interface CompRow {
    name: string;
    desc: string;
    freq: string;              // selected frequency option
    threshold: string;         // "Min:" value
    pills: Pill[];             // trailing scope + priority pills
    checked?: boolean;         // toggle on by default (default true)
    sub?: boolean;             // indented sub-component row
    disabled?: boolean;        // permanently disabled component (locked controls)
    freqOptions?: string[];    // restricted option list (disabled rows)
}

export interface GlobalSection {
    id: string;
    dot: string;               // color of the leading status dot
    title: string;             // includes leading emoji
    count: { text: string; className: string; style?: CSSProperties };
    freqBadge: { text: string; className: string };
    open?: boolean;
    rows: CompRow[];
}

export const FREQ = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];

export const GLOBAL_SECTIONS: GlobalSection[] = [
    {
        id: 'section-1',
        dot: '#1b52c4',
        title: '📊 Profile Data Coverage',
        count: { text: '8 components', className: 'badge bg-primary-subtle text-primary font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
        freqBadge: { text: 'Monthly Default', className: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-1 rounded' },
        open: true,
        rows: [
            { name: 'Total Alumni Onboarded', desc: 'Total profiles on the platform', freq: 'Monthly', threshold: 'Always', pills: [BOTH, HIGH] },
            { name: 'Active Engagement Rate (%)', desc: '% of alumni who logged in or took action in the period', freq: 'Monthly', threshold: '1 active', pills: [BOTH, HIGH] },
            { name: 'Batch-wise Alumni Bifurcation', desc: 'Count by pass-out year', freq: 'Quarterly', threshold: '3 batches', pills: [BOTH, HIGH] },
            { name: 'Profile Completeness Score', desc: 'Avg % of profile fields filled', freq: 'Monthly', threshold: 'Always', pills: [BOTH, HIGH] },
            { name: 'India vs Abroad Split', desc: '% alumni based domestically vs internationally', freq: 'Quarterly', threshold: '10 profiles', pills: [BOTH, MEDIUM] },
            { name: 'Alumni Status Breakdown', desc: 'Working / Pursuing / Completed split', freq: 'Monthly', threshold: '5 profiles', pills: [BOTH, HIGH] },
            { name: 'Student Data Coverage', desc: 'Student profiles count & completeness', freq: 'Monthly', threshold: 'Module ON', pills: [K12, HIGH] },
            { name: 'New Profiles Added', desc: 'Net new profiles created in the period', freq: 'Monthly', threshold: '1 profile', pills: [BOTH, MEDIUM] },
        ],
    },
    {
        id: 'section-2',
        dot: '#7c3aed',
        title: '🎓 Academic & Professional Insights',
        count: { text: '8 components', className: 'badge font-10 font-500 px-2 py-0.5 rounded-pill ms-1', style: { backgroundColor: '#ede9fe', color: '#7c3aed' } },
        freqBadge: { text: 'Quarterly Default', className: 'badge bg-primary-subtle text-primary font-10 font-500 px-2 py-1 rounded' },
        rows: [
            { name: 'Top Courses Pursued', desc: 'Top 5 streams at UG level by %', freq: 'Quarterly', threshold: '10 profiles', pills: [BOTH, HIGH] },
            { name: 'Notable Alumni Highlight', desc: '1–3 featured alumni, AI-suggested + SM-approved', freq: 'Monthly', threshold: '1 notable', pills: [BOTH, HIGH] },
            { name: 'Recent Achievement / News', desc: 'Promotions, awards, publications in period', freq: 'Monthly', threshold: '1 event', pills: [BOTH, HIGH] },
            { name: 'Working Alumni & Top Companies', desc: 'Total working count + top 5 employers', freq: 'Quarterly', threshold: '10 working', pills: [BOTH, MEDIUM] },
            { name: 'Alumni at Prestigious Colleges', desc: 'IITs, IIMs, Oxbridge, Ivy League etc.', freq: 'Monthly', threshold: '1 alumni', pills: [BOTH, HIGH] },
        ],
    },
    {
        id: 'section-3',
        dot: '#64748b',
        title: '📞 Outreach Activities',
        count: { text: '10 components', className: 'badge bg-secondary bg-opacity-10 text-secondary font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
        freqBadge: { text: 'Monthly Default', className: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-1 rounded' },
        rows: [
            { name: 'Total Outreach Attempts', desc: 'All touchpoints by Univariety team', freq: 'Monthly', threshold: '1 attempt', pills: [BOTH, HIGH] },
            { name: 'Outreach Coverage (%)', desc: '% of alumni reached out to', freq: 'Monthly', threshold: '1 alumni', pills: [BOTH, HIGH] },
            { name: 'Total Commitments Captured (Summary)', desc: 'Aggregate of all pledge types — shown as summary strip (Total / Confirmed / Follow-up)', freq: 'Monthly', threshold: '1 pledge', pills: [BOTH, HIGH] },
            { name: '↳ Mentorship Commitments', desc: 'Alumni pledging to mentor students', freq: 'Monthly', threshold: '1 pledge', pills: [BOTH, HIGH], sub: true },
            { name: '↳ Recruitment Commitments', desc: 'Alumni open to hiring / interviewing students', freq: 'Monthly', threshold: '1 pledge', pills: [BOTH, HIGH], sub: true },
            { name: '↳ Guest Lecture / Panel Commitments', desc: 'Alumni agreed to speak at events or webinars', freq: 'Monthly', threshold: '1 pledge', pills: [BOTH, MEDIUM], sub: true },
            { name: '↳ Donation Pledges', desc: 'Financial or in-kind donations pledged', freq: 'Monthly', threshold: '1 pledge', pills: [BOTH, MEDIUM], sub: true },
            { name: 'Commitment Conversion Rate (YTD)', desc: '% of total YTD pledges that have been fulfilled', freq: 'Quarterly', threshold: '5 YTD', pills: [BOTH, MEDIUM] },
            { name: 'Outreach by Channel', desc: 'Breakdown by Phone / WhatsApp / AI Bot / Zoom / Email', freq: 'Monthly', threshold: '2 channels', pills: [BOTH, MEDIUM] },
        ],
    },
    {
        id: 'section-4',
        dot: '#0891b2',
        title: '📣 Campaign & Event Activities',
        count: { text: '8 components', className: 'badge bg-info-subtle text-info font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
        freqBadge: { text: 'Monthly Default', className: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-1 rounded' },
        rows: [
            { name: 'Email Campaigns Sent + Open Rate', desc: 'Campaign count, open rate, click rate', freq: 'Monthly', threshold: '1 campaign', pills: [BOTH, HIGH] },
            { name: 'WhatsApp Campaigns + Read/Reply Rate', desc: 'Delivery, read %, reply %', freq: 'Monthly', threshold: '1 broadcast', pills: [BOTH, HIGH] },
            { name: 'Events: Webinars + Physical', desc: 'Count, registrations, attendance', freq: 'Monthly', threshold: '1 event', pills: [BOTH, HIGH] },
            { name: 'Email Unsubscribe Rate', desc: 'Flag if > 0.5%', freq: 'Quarterly', threshold: '> 0.5%', pills: [BOTH, LOW], checked: false, disabled: true, freqOptions: ['Quarterly'] },
        ],
    },
    {
        id: 'section-5',
        dot: '#0f9d58',
        title: '💻 Platform Activity Summary',
        count: { text: '6 components', className: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
        freqBadge: { text: 'Monthly Default', className: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-1 rounded' },
        rows: [
            { name: 'Platform Logins', desc: 'Total alumni/student logins in period', freq: 'Monthly', threshold: '5 logins', pills: [BOTH, HIGH] },
            { name: 'Student–Alumni Conversations', desc: '1:1 conversations on platform', freq: 'Monthly', threshold: '5 chats', pills: [BOTH, HIGH] },
            { name: 'Guidance Videos Watched', desc: 'Total video views by students', freq: 'Monthly', threshold: '10 views', pills: [BOTH, HIGH] },
            { name: 'Counselling Sessions', desc: 'Requires counselling package to be active', freq: 'Monthly', threshold: 'Pkg ON', pills: [BOTH, HIGH] },
            { name: 'Job / Internship Posts', desc: 'Alumni-posted opportunities', freq: 'Monthly', threshold: '1 post', pills: [BOTH, HIGH] },
            { name: 'Photo Albums / Memories Added', desc: 'Albums uploaded by alumni — emotional engagement signal', freq: 'Quarterly', threshold: '1 album', pills: [BOTH, LOW] },
        ],
    },
];

// ── AUDIT LOG ──────────────────────────────────────────────────
export type LogType = 'system' | 'override' | 'schedule' | 'global' | 'approval' | 'send';

export const ICON_BGS: Record<LogType, string> = {
    send: '#d1fae5', override: '#fef3c7', system: '#ebf2ff', global: '#ede9fe', schedule: '#cffafe', approval: '#fce7f3',
};
export const TAG_STYLES: Record<LogType, CSSProperties> = {
    send: { backgroundColor: '#d1fae5', color: '#065f46' },
    override: { backgroundColor: '#fef3c7', color: '#92400e' },
    system: { backgroundColor: '#ebf2ff', color: '#1b52c4' },
    global: { backgroundColor: '#ede9fe', color: '#5b21b6' },
    schedule: { backgroundColor: '#cffafe', color: '#0891b2' },
    approval: { backgroundColor: '#fce7f3', color: '#9d174d' },
};

export const LOG_FILTERS: { key: LogType | 'all'; label: string; className: string; activeStyle?: CSSProperties }[] = [
    { key: 'all', label: 'All Events', className: 'btn-outline-secondary' },
    { key: 'send', label: '✅ Sends', className: 'btn-outline-success' },
    { key: 'override', label: '🔧 Overrides', className: 'btn-outline-warning text-warning-emphasis' },
    { key: 'system', label: '🤖 System', className: 'btn-outline-primary' },
    { key: 'global', label: '⚙️ Global Settings', className: 'btn-outline-secondary', activeStyle: { borderColor: '#7c3aed', color: '#7c3aed' } },
    { key: 'schedule', label: '📅 Schedule', className: 'btn-outline-info' },
];
